if (!customElements.get('zentron-scope-lookup')) {
  customElements.define('zentron-scope-lookup', class extends HTMLElement {
    connectedCallback() {
      this.initialize();
    }

    initialize() {
      if (this.initialized) return true;
      this.brand = this.querySelector('[data-zf-lookup-brand]');
      this.model = this.querySelector('[data-zf-lookup-model]');
      this.search = this.querySelector('[data-zf-lookup-search]');
      this.diameter = this.querySelector('[data-zf-lookup-diameter]');
      this.sourceField = this.querySelector('[data-zf-lookup-source]');
      this.sourceLink = this.querySelector('[data-zf-lookup-source-link]');
      this.result = this.querySelector('[data-zf-lookup-result]');
      this.resultValue = this.querySelector('[data-zf-lookup-value]');
      this.precisionNote = this.querySelector('[data-zf-lookup-precision-note]');
      this.status = this.querySelector('[data-zf-lookup-status]');
      if (!this.brand || !this.model || !this.diameter || !this.sourceField) return false;

      const initialBrand = this.brand.value;
      const initialModel = this.model.value;
      this.brandPlaceholder = this.placeholderFor(this.brand);
      this.modelPlaceholder = this.placeholderFor(this.model, this.dataset.modelPlaceholder);
      this.readData();
      this.initialized = true;
      this.renderBrands(initialBrand);
      this.renderModels(initialModel);
      this.syncSelection();

      this.brand.addEventListener('change', () => {
        if (this.search) this.search.value = '';
        this.renderModels('');
        this.syncSelection();
      });
      this.model.addEventListener('change', () => this.syncSelection());
      this.search?.addEventListener('input', () => this.syncSelection());
      this.querySelector('[data-zf-lookup-fallback]')?.addEventListener('click', event => {
        event.preventDefault();
        const guide = this.closest('zentron-fitment');
        const radio = guide?.querySelector('input[type="radio"][name="measurement_status"][value="need_help"]');
        if (!radio) return;
        const row = this.selectionRow();
        const opticModel = guide.querySelector('input[name="optic_model"]');
        if (row && opticModel) {
          opticModel.value = row.model;
          this.clearParentError(opticModel);
        }
        this.alignOpticBrand();
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        this.clearParentError(radio);
        radio.focus();
      });
      return true;
    }

    readData() {
      this.rows = [];
      this.source = null;
      try {
        const data = JSON.parse(this.querySelector('[data-zf-scope-data]')?.textContent || '');
        const text = value => typeof value === 'string' && value.trim().length > 0;
        this.source = this.normalizeSource(data?.source);
        const candidates = (Array.isArray(data?.scopes) ? data.scopes : []).filter(row => row && text(row.id) && text(row.brand) && text(row.model)
          && typeof row.outsideDiameterMm === 'number' && Number.isFinite(row.outsideDiameterMm) && row.outsideDiameterMm > 0
          && (row.sourcePage == null || Number.isInteger(row.sourcePage) && row.sourcePage > 0))
          .map(row => ({
            ...row,
            source: Object.prototype.hasOwnProperty.call(row, 'source') ? this.normalizeSource(row.source) : this.source,
            approximation: this.normalizeApproximation(row)
          }))
          .filter(row => row.source && (row.approximate !== true || row.approximation));
        const idCounts = new Map();
        candidates.forEach(row => idCounts.set(row.id.trim(), (idCounts.get(row.id.trim()) || 0) + 1));
        this.rows = candidates.filter(row => idCounts.get(row.id.trim()) === 1).map(row => ({
          id: row.id.trim(),
          brand: row.brand.trim(),
          model: row.model.trim(),
          outsideDiameterMm: row.outsideDiameterMm,
          sourcePage: row.sourcePage || row.source.page,
          source: row.source,
          approximate: row.approximate === true,
          sourceDimension: row.approximation?.sourceDimension || null,
          displayDiameterMm: row.approximation?.displayDiameterMm || null,
          requiresMeasurement: row.requiresMeasurement === true,
          conflictingDiametersMm: Array.isArray(row.conflictingDiametersMm)
            ? row.conflictingDiametersMm.filter(value => typeof value === 'number' && Number.isFinite(value) && value > 0)
            : []
        }));
      } catch (_) {
        this.source = null;
      }
      this.brands = [...new Set(this.rows.map(row => row.brand))].sort((a, b) => this.compare(a, b));
      this.rowById = new Map(this.rows.map(row => [row.id, row]));
    }

    normalizeApproximation(row) {
      if (row.approximate !== true) return null;
      const numberText = value => typeof value === 'string' && /^(?:\d+(?:\.\d+)?|\.\d+)$/.test(value.trim())
        && Number.isFinite(Number(value)) && Number(value) > 0;
      const dimension = row.sourceDimension;
      if (!['in', 'mm'].includes(dimension?.unit) || !numberText(dimension.value)) return null;
      const sourceValue = dimension.value.trim();
      if (Math.abs(Number(sourceValue) * (dimension.unit === 'in' ? 25.4 : 1) - row.outsideDiameterMm) > 1.3 + 1e-9) return null;
      const displayDiameterMm = row.displayDiameterMm == null ? String(row.outsideDiameterMm) : row.displayDiameterMm;
      if (!numberText(displayDiameterMm) || Math.abs(Number(displayDiameterMm) - row.outsideDiameterMm) > 1.3 + 1e-9) return null;
      return {
        sourceDimension: { value: sourceValue, unit: dimension.unit },
        displayDiameterMm: displayDiameterMm.trim()
      };
    }

    normalizeSource(source) {
      const text = value => typeof value === 'string' && value.trim().length > 0;
      if (!source || !text(source.title) || !text(source.url)) return null;
      try {
        const url = new URL(source.url.trim());
        if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return null;
        const page = source.page ?? source.sourcePage;
        if (page != null && (!Number.isInteger(page) || page <= 0)) return null;
        return {
          title: source.title.trim(),
          url: url.href,
          referenceDate: text(source.referenceDate) ? source.referenceDate.trim() : '',
          publisher: text(source.publisher) ? source.publisher.trim() : '',
          sourceType: text(source.sourceType) ? source.sourceType.trim() : '',
          page: page || null
        };
      } catch (_) {
        return null;
      }
    }

    sourceDescription(row) {
      const source = row.source;
      return `${source.title}${source.referenceDate ? ` (${source.referenceDate})` : ''}${row.sourcePage ? `, p. ${row.sourcePage}` : ''}`;
    }

    showSource(row) {
      this.sourceField.value = this.sourceDescription(row);
      if (!this.sourceLink) return;
      this.sourceLink.setAttribute('href', row.source.url);
      const label = typeof this.dataset.sourceLabel === 'string' ? this.dataset.sourceLabel.trim() : '';
      this.sourceLink.textContent = label
        ? `${row.source.publisher ? `${row.source.publisher} · ` : ''}${label} ↗`
        : `${row.source.title}${row.source.referenceDate ? ` · ${row.source.referenceDate}` : ''} ↗`;
      this.sourceLink.setAttribute('title', this.sourceDescription(row));
      this.sourceLink.hidden = false;
    }

    compare(a, b) {
      return a.localeCompare(b, 'en', { sensitivity: 'base', numeric: true });
    }

    normalize(value) {
      return String(value || '').normalize('NFKC').toLowerCase()
        .replace(/[×✕]/g, 'x')
        .replace(/[\u2010-\u2015\u2212]/g, '-')
        .replace(/(\d),(?=\d)/g, '$1.')
        .replace(/\s+/g, '');
    }

    placeholderFor(select, fallback = '') {
      const existing = [...select.options].find(option => option.value === '');
      if (existing) return existing.cloneNode(true);
      return this.option('', fallback);
    }

    option(value, label) {
      const option = this.ownerDocument.createElement('option');
      option.value = value;
      option.textContent = label;
      option.dataset.zfDisplay = label;
      return option;
    }

    renderBrands(preferred = '') {
      this.brand.replaceChildren(this.brandPlaceholder.cloneNode(true), ...(this.brands || []).map(brand => this.option(brand, brand)));
      this.brand.value = (this.brands || []).includes(preferred) ? preferred : '';
    }

    matchingRows() {
      const query = this.normalize(this.search?.value);
      return this.rows.filter(row => row.brand === this.brand.value && (!query || this.normalize(row.model).includes(query)))
        .sort((a, b) => this.compare(a.model, b.model));
    }

    renderModels(preferred = '') {
      const rows = this.matchingRows();
      this.model.replaceChildren(this.modelPlaceholder.cloneNode(true), ...rows.map(row => this.option(row.id, row.model)));
      this.model.value = rows.some(row => row.id === preferred) ? preferred : '';
      return rows;
    }

    clearResult() {
      this.diameter.value = '';
      this.sourceField.value = '';
      if (this.sourceLink) {
        this.sourceLink.removeAttribute('href');
        this.sourceLink.removeAttribute('title');
        this.sourceLink.textContent = '';
        this.sourceLink.hidden = true;
      }
      if (this.resultValue) this.resultValue.textContent = '';
      if (this.result) this.result.hidden = true;
      if (this.precisionNote) this.precisionNote.hidden = true;
    }

    setStatus(message = '') {
      if (this.status && this.status.textContent !== message) this.status.textContent = message;
    }

    alignOpticBrand() {
      if (!this.brand.value) return;
      const radios = [...(this.closest('zentron-fitment')?.querySelectorAll('input[type="radio"][name="optic_brand"]') || [])];
      const normalized = this.normalize(this.brand.value);
      const match = radios.find(radio => this.normalize(radio.dataset.zfDisplay) === normalized);
      radios.forEach(radio => { radio.checked = radio === match; });
    }

    clearParentError(field) {
      const guide = this.closest('zentron-fitment');
      if (typeof guide?.clearError === 'function') guide.clearError(field);
    }

    selectionRow() {
      const row = this.rowById?.get(this.model.value);
      return row && row.brand === this.brand.value && this.matchingRows().some(candidate => candidate.id === row.id) ? row : null;
    }

    requiresMeasurement() {
      if (!this.initialize()) return false;
      return this.selectionRow()?.requiresMeasurement === true;
    }

    validSelection() {
      if (!this.initialize()) return false;
      const row = this.selectionRow();
      return Boolean(row && !row.requiresMeasurement);
    }

    restoreSelection(fields) {
      if (!this.initialize()) return;
      const saved = new Map((Array.isArray(fields) ? fields : [])
        .filter(field => field && typeof field.name === 'string' && typeof field.value === 'string')
        .map(field => [field.name, field.value]));
      const brand = saved.get('reference_scope_brand') || '';
      const id = saved.get('reference_scope_model') || '';
      if (this.search) this.search.value = '';
      this.brand.value = (this.brands || []).includes(brand) ? brand : '';
      const row = this.rowById?.get(id);
      this.renderModels(row?.brand === this.brand.value ? id : '');
      this.syncSelection();
    }

    syncSelection() {
      if (!this.initialize()) return;
      const id = this.model.value;
      if (!(this.brands || []).includes(this.brand.value)) this.brand.value = '';
      const rows = this.renderModels(id);
      this.clearResult();
      this.alignOpticBrand();
      if (this.brand.value) this.clearParentError(this.brand);
      if (!this.rows.length || (this.brand.value && rows.length === 0)) {
        this.setStatus(this.dataset.emptyMessage || '');
        return;
      }
      this.setStatus('');
      const row = this.selectionRow();
      if (!row) return;
      this.showSource(row);
      if (row.requiresMeasurement) {
        this.setStatus(this.dataset.conflictMessage || '');
        return;
      }
      const diameter = row.approximate
        ? `≈${row.displayDiameterMm} mm${row.sourceDimension.unit === 'in' ? ` (${row.sourceDimension.value} in)` : ''}`
        : `${row.outsideDiameterMm} mm`;
      this.diameter.value = diameter;
      if (this.resultValue) this.resultValue.textContent = diameter;
      if (this.result) this.result.hidden = false;
      if (this.precisionNote) this.precisionNote.hidden = !row.approximate;
      this.clearParentError(this.model);
      this.clearParentError(this.diameter);
    }
  });
}
